import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useAdmin } from '@vtex/raccoon-next'
import { catchLogger } from 'shared'
import {
  CalculatorConfigurationPromotion,
  PROMOTION_TYPE,
  PromotionStatus,
  type BenefitsResponseData,
  type CalculatorType,
  type SimplifiedPromotion,
} from 'typings'
import { useFetch } from './useFetch'

const calculatorTypes: string[] = [
  ...Object.values(PROMOTION_TYPE),
  'maxPricePerItem',
  'tax',
  'multipleEffects',
]

function getStatus({
  isActive,
  beginDate,
  endDate,
}: CalculatorConfigurationPromotion): PromotionStatus {
  const now = new Date()
  const beginDateObj = new Date(beginDate)
  const endDateObj = endDate ? new Date(endDate) : undefined

  if (endDateObj && endDateObj.getTime() < now.getTime()) {
    return 'completed'
  }

  if (isActive) {
    if (beginDateObj.getTime() > now.getTime()) {
      return 'scheduled'
    }

    return 'running'
  }

  return 'paused'
}

function isCalculatorType(type: string): type is CalculatorType {
  return calculatorTypes.includes(type)
}

export function mapPromotionType(type: string): CalculatorType {
  const promotionType = (type[0].toLowerCase() + type.slice(1)).trim()

  if (isCalculatorType(promotionType)) {
    return promotionType
  }

  throw new Error(`Invalid promotion type: ${promotionType}`)
}

const toSimplifiedPromotion = (
  data: CalculatorConfigurationPromotion
): SimplifiedPromotion => ({
  isArchived: false,
  beginDate: data.beginDate,
  description: data.description,
  endDate: data.endDate,
  id: data.idCalculatorConfiguration,
  isActive: data.isActive,
  status: getStatus(data),
  lastModified: data.lastModifiedUtc,
  name: data.name,
  salesChannels: {
    exclusive: data.areSalesChannelIdsExclusive,
    ids: data.idsSalesChannel,
  },
  type: mapPromotionType(data.type),
  utmCampaign: data.utmCampain?.split(',').filter((x) => x) ?? [],
  utmSource: data.utmSource?.split(',').filter((x) => x) ?? [],
})

type UsePromotionsReturn = {
  meta: {
    limit: number
    usage: number
  }
  items: SimplifiedPromotion[]
}

export function usePromotions(options?: UseQueryOptions<UsePromotionsReturn>) {
  const { fetcherWrapper } = useFetch()
  const admin = useAdmin()

  const url = new URL(
    `/api/proxy/rnb/pvt/benefits/calculatorconfiguration`,
    window.location.origin
  ).toString()

  async function fetcher() {
    return fetcherWrapper(url)
      .then((r) => r.json())
      .then((response: BenefitsResponseData) => {
        const items = catchLogger({
          id: 'map-simplified-promotions',
          fn: () => response.items.map(toSimplifiedPromotion),
          admin,
        })

        return {
          meta: {
            limit: response.limitConfiguration.limit,
            usage: response.limitConfiguration.activesCount,
          },
          items,
        }
      })
  }

  return useQuery<UsePromotionsReturn>({
    queryKey: ['active-promotions'],
    queryFn: fetcher,
    ...options,
  })
}
